<!--
  The next N weeks for this audience: what's booked, and which weeks are empty.

  "Booked" means a campaign whose Customer.io work areas overlap this audience's — so an industry sees its
  topics' campaigns, which is who actually receives them. Open slots come from the same scheduler caps that
  will be enforced when a campaign is saved, so a week showing 0 open email slots really is full.

  The plan's open suggestions sit in the week they are planned for, alongside what's booked, so the marketer can
  see when a plan will land before building any of it. They open the same card the Suggestions tab uses, so every
  action on a card is available here without a second copy of it.
-->
<template>
  <div>
    <div class="d-flex align-center flex-wrap mb-3">
      <div class="text-caption grey--text mr-3">
        {{ summary }}
      </div>
      <v-spacer />
      <v-switch
        v-if="suggestions.length"
        v-model="showSuggestions"
        dense
        hide-details
        class="mt-0 pt-0"
        :label="`Show suggestions (${suggestions.length})`"
      />
    </div>

    <v-timeline
      dense
      class="pt-0"
    >
      <v-timeline-item
        v-for="(week, index) in weeks"
        :key="week.weekStart"
        small
        :color="week.isHole ? 'grey lighten-1' : 'mcsprimary'"
        :icon="week.isHole ? 'mdi-calendar-blank-outline' : ''"
      >
        <div class="d-flex align-center flex-wrap">
          <strong class="text-body-2 mr-3">Week of {{ weekLabel(week.weekStart) }}</strong>
          <v-chip
            v-if="week.emailOpenSlots"
            x-small
            outlined
            class="mr-1"
          >
            {{ week.emailOpenSlots }} email slot{{ week.emailOpenSlots === 1 ? '' : 's' }} open
          </v-chip>
          <v-chip
            v-if="week.socialOpenSlots"
            x-small
            outlined
          >
            {{ week.socialOpenSlots }} social slot{{ week.socialOpenSlots === 1 ? '' : 's' }} open
          </v-chip>
        </div>

        <div
          v-if="week.isHole"
          class="text-caption grey--text"
        >
          {{ weekSuggestions(index).length ? 'Nothing booked yet.' : 'Nothing planned for this audience.' }}
        </div>

        <div
          v-for="entry in week.entries"
          :key="entry.campaignId + '-' + entry.scheduledSend"
          class="d-flex align-center mt-1"
        >
          <PlatformIcon
            v-if="entry.platform"
            :platform="entry.platform"
            :size="18"
            class="mr-2"
          />
          <v-icon
            v-else
            small
            class="mr-2"
          >
            mdi-email-outline
          </v-icon>

          <a
            class="text-body-2 mr-2"
            href="#"
            @click.prevent="openCampaign(entry.campaignId)"
          >{{ entry.campaignName }}</a>

          <v-chip
            x-small
            :color="itemStatus(entry.status).color"
            dark
            class="mr-2"
          >
            {{ itemStatus(entry.status).label }}
          </v-chip>

          <span class="text-caption grey--text">{{ sendLabel(entry.scheduledSend) }}</span>

          <v-chip
            v-if="entry.fromPlan"
            x-small
            color="primary"
            dark
            class="ml-2"
            title="Built from this audience's plan"
          >
            from plan
          </v-chip>

          <v-chip
            v-if="!entry.directTarget"
            x-small
            outlined
            class="ml-2"
            title="Targets a related subject, but reaches this audience"
          >
            indirect
          </v-chip>
        </div>

        <SuggestionRow
          v-for="item in weekSuggestions(index)"
          :key="'s-' + item.id"
          :item="item"
          :note="overdueNote(item, index)"
          @open="openItemId = item.id"
        />
      </v-timeline-item>

      <!-- Planned past the roadmap's window — still on the plan, just further out than the calendar shows. -->
      <v-timeline-item
        v-if="laterSuggestions.length"
        small
        color="grey lighten-1"
        icon="mdi-dots-horizontal"
      >
        <strong class="text-body-2">Later</strong>
        <SuggestionRow
          v-for="item in laterSuggestions"
          :key="'s-' + item.id"
          :item="item"
          :note="'Week of ' + weekOf(itemDate(item))"
          @open="openItemId = item.id"
        />
      </v-timeline-item>
    </v-timeline>

    <!--
      The live item, looked up by id on every render: an action reloads the plan, and the dialog has to show
      what the item is now (approved, built, dismissed) rather than the copy it was opened with.
    -->
    <v-dialog
      :value="!!openItem"
      max-width="520"
      @input="(v) => { if (!v) openItemId = 0; }"
    >
      <SuggestionCard
        v-if="openItem"
        :item="openItem"
        @changed="$emit('changed')"
      />
    </v-dialog>
  </div>
</template>

<script>
import PlatformIcon from '@/components/common/PlatformIcon';
import SuggestionCard from '@/components/marketing/audiences/SuggestionCard';
import SuggestionRow from '@/components/marketing/audiences/SuggestionRow';
import { itemStatus, weekOf } from '@/helpers/marketing';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export default {
  name: 'AudienceRoadmap',
  components: { PlatformIcon, SuggestionCard, SuggestionRow },
  props: {
    roadmap: {
      type: Object,
      default: () => ({})
    },
    // The plan's items. Only the ones not yet on the calendar are drawn — a built campaign is already a
    // booked entry above, and drawing it twice would double-count the week.
    items: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showSuggestions: true,
      openItemId: 0
    };
  },
  computed: {
    weeks() {
      return this.roadmap.weeks || [];
    },
    weekStarts() {
      return this.weeks.map((w) => new Date(w.weekStart).getTime());
    },
    // Open suggestions with a week to sit in: not built into a campaign, not finished, not turned down.
    suggestions() {
      const ids = this.$enums.AUDIENCE.ITEM_STATUS_IDS;
      return this.items
        .filter((i) => !i.campaignId && i.status !== ids.DONE && i.status !== ids.DISMISSED)
        .filter((i) => this.itemDate(i))
        .sort((a, b) => this.itemDate(a) - this.itemDate(b));
    },
    // Suggestions bucketed by roadmap week index; -1 holds the ones planned past the last week shown.
    buckets() {
      const map = {};
      if (!this.showSuggestions || !this.weekStarts.length) return map;

      const last = this.weekStarts[this.weekStarts.length - 1];
      this.suggestions.forEach((item) => {
        const t = this.itemDate(item).getTime();
        let index;
        if (t >= last + WEEK_MS) {
          index = -1;
        } else {
          // Anything planned before the first week shown has slipped; it lands in the current week, flagged.
          index = Math.max(0, this.weekStarts.filter((start) => start <= t).length - 1);
        }
        (map[index] = map[index] || []).push(item);
      });
      return map;
    },
    laterSuggestions() {
      return this.buckets[-1] || [];
    },
    openItem() {
      return this.openItemId ? this.items.find((i) => i.id === this.openItemId) || null : null;
    },
    summary() {
      const total = this.weeks.reduce((n, w) => n + (w.entries || []).length, 0);
      const holes = this.weeks.filter((w) => w.isHole).length;
      if (!this.weeks.length) return '';

      // Said either way: planned weeks are the plan's intent, and the scheduler decides the real day at build.
      const pending = this.suggestions.length
        ? ` ${this.suggestions.length} more suggested by the plan, shown in their planned week — the weekly ` +
          'caps set the real send date when each is built.'
        : '';
      if (!total) return `Nothing is scheduled for this audience in the next ${this.weeks.length} weeks.${pending}`;

      const fromPlan = this.weeks.reduce(
        (n, w) => n + (w.entries || []).filter((e) => e.fromPlan).length,
        0
      );
      const planNote = fromPlan ? `, ${fromPlan} from the plan` : '';
      return `${total} scheduled item${total === 1 ? '' : 's'} over ${this.weeks.length} weeks${planNote}, with ${holes} empty week${holes === 1 ? '' : 's'}.${pending}`;
    }
  },
  methods: {
    itemStatus,
    weekOf,
    // The week an item is planned for; a seasonal item with no planned week falls back to its occasion date.
    itemDate(item) {
      const raw = (item.detail || {}).plannedDate || item.suggestedDate;
      if (!raw) return null;
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? null : d;
    },
    weekSuggestions(index) {
      return this.buckets[index] || [];
    },
    // Only the current week can hold an item whose planned week has passed — say so rather than pretend.
    overdueNote(item, index) {
      if (index !== 0 || !this.weekStarts.length) return '';
      return this.itemDate(item).getTime() < this.weekStarts[0]
        ? `was planned for ${weekOf(this.itemDate(item))}`
        : '';
    },
    weekLabel(iso) {
      return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
    sendLabel(iso) {
      return new Date(iso).toLocaleString(undefined, {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit'
      });
    },
    // New tab, same as the suggestion cards: the roadmap is the thing being reviewed, so following a campaign
    // out of it and back again loses the admin's place.
    openCampaign(id) {
      const href = this.$router.resolve(`/marketing/campaigns/${id}`).href;
      window.open(href, '_blank', 'noopener');
    }
  }
};
</script>
